import { useEffect, useState } from "react";
import {
  DistributedMaterial,
  Employee,
  EmployeeService,
  WorkDay,
} from "../../api/generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { ymdDash } from "../search_bar";
import { DistributionForm, DistributionMode } from "./distributionForm";

interface MaterialIssueFormProps {
  employee: Employee;
  mode: DistributionMode;
  onSave: () => void;
}

type MaterialIssueFormState =
  | { state: "LOADING" }
  | {
      state: "READY";
      workSchedule: WorkDay;
      distributedMaterials: DistributedMaterial[];
    }
  | { state: "NOT_FOUND" }
  | { state: "ERROR"; error: Error };

export const MaterialIssueForm = (props: MaterialIssueFormProps) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [state, setState] = useState<MaterialIssueFormState>({
    state: "LOADING",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const date = format(selectedDate, ymdDash);
        const workSchedule = await EmployeeService.getEmployeeSchedule(
          props.employee.id!,
          date,
        );
        if (!workSchedule) {
          setState({ state: "NOT_FOUND" });
          return;
        }
        const distributedMaterials =
          await EmployeeService.getEmployeeScheduleDistributed(
            props.employee.id!,
            workSchedule.id!,
          );
        if (!distributedMaterials) {
          setState({ state: "NOT_FOUND" });
          return;
        }
        setState({
          state: "READY",
          workSchedule,
          distributedMaterials: distributedMaterials.distributedMaterials,
        });
      } catch (error) {
        setState({ state: "ERROR", error: error as Error });
      }
    };

    setState({ state: "LOADING" });
    fetchData();
  }, [props.employee.id, selectedDate]);

  const changeDate = (days: number) => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + days);
      return newDate;
    });
  };

  const formattedDate = selectedDate.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const notFoundMessage =
    props.mode === "return"
      ? "Geen werkrooster of uitgiftes gevonden voor deze datum"
      : "Geen werkrooster gevonden voor deze datum";

  return (
    <div className="flex w-full flex-col justify-center items-center mb-4">
      <div className="flex w-1/2 justify-between items-center mb-4">
        <button className="btn btn-secondary" onClick={() => changeDate(-1)}>
          ⬅ Vorige dag
        </button>
        <h2 className="text-lg font-semibold">{formattedDate}</h2>
        <button className="btn btn-secondary" onClick={() => changeDate(1)}>
          Volgende dag ➡
        </button>
      </div>

      {state.state === "LOADING" && (
        <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
      )}

      {state.state === "ERROR" && (
        <div role="alert" className="alert alert-error w-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>Kon geen rooster ophalen: {state.error.message}</span>
        </div>
      )}

      {state.state === "NOT_FOUND" && (
        <div role="alert" className="alert alert-info w-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{notFoundMessage}</span>
        </div>
      )}

      {state.state === "READY" && (
        <DistributionForm
          employee={props.employee}
          workSchedule={state.workSchedule}
          distributedMaterials={state.distributedMaterials}
          mode={props.mode}
          onSave={props.onSave}
        />
      )}
    </div>
  );
};
